/**
 * Notification Center — bell icon with dropdown for invites, whispers, accords
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { PendingInvite, WhisperEntry } from './use-backend';
import './notification-center.css';

// ─── Types ─────────────────────────────────────────────────────────────────

interface NotificationItem {
  id: string;
  type: 'invite' | 'whisper' | 'accord';
  title: string;
  body: string;
  timestamp: number;
  read: boolean;
}

export interface NotificationCenterProps {
  pendingInvites?: PendingInvite[];
  whispers?: WhisperEntry[];
  onOpenPanel: (type: string) => void;
}

// ─── LocalStorage helpers ───────────────────────────────────────────────────

const LS_KEY = 'concord-notif-read';

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function persistReadIds(ids: Set<string>): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...ids]));
  } catch {
    // storage quota — ignore
  }
}

// ─── Relative time ─────────────────────────────────────────────────────────

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Short ID display ───────────────────────────────────────────────────────

function shortId(id: string): string {
  return id.slice(0, 8);
}

// ─── Component ─────────────────────────────────────────────────────────────

export function NotificationCenter({
  pendingInvites = [],
  whispers = [],
  onOpenPanel,
}: NotificationCenterProps) {
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(() => getReadIds());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Build notification items from props
  const items: NotificationItem[] = [
    ...pendingInvites.map(
      (invite): NotificationItem => ({
        id: `invite-${invite.regionId}`,
        type: 'invite',
        title: `New invite from ${shortId(invite.inviterId)}`,
        body: `Invited you to a private conversation`,
        timestamp: Date.now(), // server doesn't expose invite timestamp yet
        read: readIds.has(`invite-${invite.regionId}`),
      })
    ),
  ];

  const unreadCount = items.filter((item) => !item.read).length;

  // Mark all visible items as read when the dropdown opens
  const markAllRead = useCallback(() => {
    const next = new Set(readIds);
    items.forEach((item) => next.add(item.id));
    setReadIds(next);
    persistReadIds(next);
  }, [items, readIds]);

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      markAllRead();
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const inviteItems = items.filter((i) => i.type === 'invite');
  const hasAny = items.length > 0;

  return (
    <div className="notif-center-wrapper" ref={wrapperRef}>
      {/* Bell button */}
      <button
        className="notif-bell-btn"
        onClick={handleOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        title="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span className="notif-badge" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="notif-dropdown" role="dialog" aria-label="Notifications">
          {/* Header */}
          <div className="notif-header">
            <span className="notif-header-title">Notifications</span>
            {hasAny && (
              <button
                className="notif-mark-read-btn"
                onClick={() => {
                  markAllRead();
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {!hasAny ? (
            /* Empty state */
            <div className="notif-empty">
              <span className="notif-empty-icon">✓</span>
              All caught up
            </div>
          ) : (
            <>
              {/* Invites section */}
              {inviteItems.length > 0 && (
                <>
                  <div className="notif-section-label">Invites</div>
                  {inviteItems.map((item) => (
                    <div
                      key={item.id}
                      className={`notif-item${!item.read ? ' notif-unread' : ''}`}
                    >
                      <span className="notif-item-icon">📨</span>
                      <div className="notif-item-content">
                        <div className="notif-item-title">{item.title}</div>
                        <div className="notif-item-body">{item.body}</div>
                        <div className="notif-item-time">{relativeTime(item.timestamp)}</div>
                      </div>
                      <button
                        className="notif-item-action"
                        onClick={() => {
                          onOpenPanel('private-chat');
                          setOpen(false);
                        }}
                      >
                        View
                      </button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
